import { forwardRef, useEffect, useRef, useState } from "react";
import { Account, } from "../../../types";
import Select, { GroupBase, MultiValue, SelectInstance, SingleValue } from "react-select";
import { fetchAccounts } from "../../../api";
import CustomModal from "../../widgets/components/CustomModal";
import AccountForm from "./AccountForm";
import { AccountCategory, AccountType } from "../../../Constants";
import { mergeSelectStyles } from "../../widgets/components/SelectStyles";

interface OptionType {
  value: string;
  label: string;
  accountType?: AccountType;
  badge?: string; // MBAI-86: small pill shown next to a synthetic option (e.g. "Pay Later")
}

// MBAI-523: fixed display order for grouping accounts by type when groupByType is set
const ACCOUNT_TYPE_GROUP_ORDER: AccountType[] = [
  AccountType.Assets,
  AccountType.Liabilities,
  AccountType.Equity,
  AccountType.Income,
  AccountType.OtherIncome,
  AccountType.Expense,
  AccountType.OtherExpense,
];

interface AccountPickerProps {
  accountType?: AccountType;
  accountCategory?: AccountCategory | AccountCategory[];
  sysAcctCode?: string;
  label?: string;
  placeholder?: string;
  tabIndex?: number;
  onAccountChange: (accountId: string | string[]) => void;
  selectedAccounts?: string[];
  isClearable?: boolean;
  isDisabled?: boolean;
  isFocused?: boolean;
  isMulti?: boolean;
  // REMOVED: rememberKey?: string;
  // REMOVED: isFormInitialized?: boolean;
  // NEW: Props for centralized default management
  defaultAccounts?: string[]; // Default values from parent
  onSaveAsDefault?: (accountIds: string[]) => Promise<boolean>; // Callback to save defaults
  showDefaultOption?: boolean; // Whether to show "Keep as default" option
  error?: string; // Validation error message
  groupByType?: boolean; // MBAI-523: group dropdown options by account type (Assets, Liabilities, ...)
  // MBAI-86: optional synthetic entry pinned above the real accounts (e.g. "Accounts Payable" on
  // Expense's Paid Through). Selecting it reports `value` through onAccountChange; the parent
  // interprets it. `value` must never collide with a real accountId.
  specialOption?: { value: string; label: string; badge?: string };
}
export const AccountPicker = forwardRef<HTMLDivElement, AccountPickerProps>(
  ({
    accountType,
    accountCategory,
    sysAcctCode,
    label,
    placeholder,
    tabIndex,
    onAccountChange,
    selectedAccounts,
    isClearable,
    isDisabled,
    isFocused,
    isMulti = false,
    // NEW props
    defaultAccounts,
    onSaveAsDefault,
    showDefaultOption = false,
    error,
    groupByType = false,
    specialOption,
  }, ref) => {
    const [accounts, setAccounts] = useState<OptionType[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);
    // REMOVED: const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false);
    // REMOVED: const [hasLoadedSystemAccount, setHasLoadedSystemAccount] = useState(false);
    const [isSavingDefault, setIsSavingDefault] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false); // NEW: Track if initial value is set
    const internalSelectRef = useRef<SelectInstance<OptionType, boolean, GroupBase<OptionType>>>(null);
    useEffect(() => {
      const getAccounts = async () => {
        try {
          const fetchedAccounts: Account[] = await fetchAccounts();
          const filteredAccounts = fetchedAccounts.filter((account) => {
            let matches = true;
            if (accountType) matches = matches && account.accountType === accountType;
            if (accountCategory) {
              matches = matches && (Array.isArray(accountCategory)
                ? accountCategory.includes(account.accountCategory)
                : account.accountCategory === accountCategory);
            }
            return matches;
          });

          const options: OptionType[] = filteredAccounts.map((account) => ({
            value: account.accountId || '',
            label: account.accountName || '',
            accountType: account.accountType,
          }));

          setAccounts(options);

          // Only set initial values once
          if (hasInitialized) return;

          // Priority 1: selectedAccounts prop (for edit mode or explicit selection)
          if (selectedAccounts && selectedAccounts.length > 0 && selectedAccounts[0]?.trim()) {
            const defaultOptions = options.filter((option) => selectedAccounts.includes(option.value));
            if (defaultOptions.length > 0) {
              setSelectedOptions(defaultOptions);
              setHasInitialized(true);
              return;
            }
          }

          // Priority 2: defaultAccounts from centralized defaults (for create mode)
          if (defaultAccounts && defaultAccounts.length > 0 && defaultAccounts[0]?.trim()) {
            const defaultOptions = options.filter((option) => defaultAccounts.includes(option.value));
            if (defaultOptions.length > 0) {
              // setSelectedOptions(defaultOptions);
              // Trigger onChange to update parent state with default value
              // onAccountChange(isMulti ? defaultAccounts : defaultAccounts[0]);
              setHasInitialized(true);
              return;
            }
          }

          // Priority 3: System account code (lowest priority)
          if (sysAcctCode) {
            const systemAccount = filteredAccounts.find(
              (account) => account.systemAccountCode === sysAcctCode
            );
            const defaultOption = options.find((option) => option.value === systemAccount?.accountId);

            if (defaultOption) {
              // setSelectedOptions([defaultOption]);
              // onAccountChange(defaultOption.value);
              setHasInitialized(true);
            }
          }

          setHasInitialized(true);
        } catch (error) {
          console.error('Error fetching accounts:', error);
        }
      };

      getAccounts();
    }, [accountType, accountCategory]); // Minimal dependencies - only re-fetch if these change
    // const toStringArray = (raw: string | null): string[] => {
    //   if (!raw) return [];
    //   try {
    //     const parsed = JSON.parse(raw);
    //     if (Array.isArray(parsed)) return parsed.filter((v): v is string => typeof v === "string");
    //     if (typeof parsed === "string") return [parsed];                    // single value stored as string
    //     if (parsed && Array.isArray(parsed.values))                         // older shape { values: [...] }
    //       return parsed.values.filter((v: unknown): v is string => typeof v === "string");
    //   } catch {
    //     // not JSON → maybe CSV or plain string
    //     const trimmed = raw.trim();
    //     if (!trimmed) return [];
    //     if (trimmed.includes(",")) return trimmed.split(",").map(s => s.trim()).filter(Boolean);
    //     return [trimmed];
    //   }
    //   return [];
    // };

    // useEffect(() => {
    //   if (!rememberKey || hasLoadedFromStorage) return;

    //   const raw = localStorage.getItem(rememberKey);
    //   const savedValues = toStringArray(raw);

    //   if (!savedValues.length) {
    //     if (raw) localStorage.removeItem(rememberKey);
    //     return;
    //   }

    //   const valueSet = new Set(savedValues);
    //   const defaultOptions = modifiedOptions.filter(opt => valueSet.has(opt.value));

    //   if (defaultOptions.length > 0) {
    //     setSelectedOptions(defaultOptions);
    //     onAccountChange(isMulti ? savedValues : defaultOptions[0].value);
    //   }
    //   //  if(selectedAccounts){
    //   //   setHasLoadedFromStorage(true); // ✅ mark as loaded
    //   //  }
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [rememberKey, isMulti, onAccountChange]);

    useEffect(() => {
      if (!selectedAccounts) return;

      const accountsArray = Array.isArray(selectedAccounts)
        ? selectedAccounts
        : [selectedAccounts];

      if (accountsArray.length === 0 || !accountsArray[0]?.trim()) {
        return;
      }

      const selectedAccountIds = accountsArray.filter(id => id?.trim());
      const matchingOptions = (specialOption ? [specialOption, ...accounts] : accounts).filter(option =>
        selectedAccountIds.includes(option.value)
      );

      if (matchingOptions.length === 0) return;

      setSelectedOptions(prev => {
        const isSame = prev.length === matchingOptions.length &&
          prev.every((opt, idx) => opt.value === matchingOptions[idx].value);
        return isSame ? prev : matchingOptions;
      });
    // specialOption is a fresh object each parent render -- key on its value only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccounts, accounts, specialOption?.value]);


    useEffect(() => {
      // Focus the select component on mount
      if (internalSelectRef.current && ref) {
        internalSelectRef.current.focus();
      }
    }, []);


    // MBAI-523: optionally group the dropdown by account type (Assets, Liabilities, ...) so
    // it's easier to find the right account when entering a long list (e.g. opening balances).
    // Off by default — existing callers keep the flat, ungrouped list they already have.
    const modifiedOptions: (OptionType | GroupBase<OptionType>)[] = groupByType
      ? [
        ...(specialOption ? [specialOption] : []),
        { value: 'add_new', label: 'Add New'.toTranslatedDisplay() },
        ...ACCOUNT_TYPE_GROUP_ORDER
          .map((type) => ({
            label: (type as string).toTranslatedDisplay(),
            options: accounts.filter((option) => option.accountType === type),
          }))
          .filter((group) => group.options.length > 0),
        ...(accounts.some((option) => !option.accountType || !ACCOUNT_TYPE_GROUP_ORDER.includes(option.accountType))
          ? [{
            label: 'Other'.toTranslatedDisplay(),
            options: accounts.filter((option) => !option.accountType || !ACCOUNT_TYPE_GROUP_ORDER.includes(option.accountType)),
          }]
          : []),
      ]
      : [
        ...(specialOption ? [specialOption] : []),
        { value: 'add_new', label: 'Add New'.toTranslatedDisplay() },
        ...accounts,
      ];

    const customStyles = mergeSelectStyles({
      option: (provided: any) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
      }),
      singleValue: (provided: any) => ({
        ...provided,
        display: 'flex',
        alignItems: 'center',
      }),
      control: (provided: any) => ({
        ...provided,
        // !important is required here: react-select's own SCSS (.react-select-styled
        // .react-select__control) has higher specificity (two classes) than the single
        // emotion class this styles function generates, so a plain borderColor override
        // is silently ignored without it (MBAI-160).
        borderColor: error ? '#f1416c !important' : provided.borderColor,
        '&:hover': {
          borderColor: error ? '#f1416c !important' : provided['&:hover']?.borderColor,
        },
      }),
    });

    const formatOptionLabel = ({ label, badge }: OptionType) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {badge && <span className="badge badge-light-warning ms-2">{badge}</span>}
      </div>
    );

    const handleSelectChange = (
      newValue: MultiValue<OptionType> | SingleValue<OptionType>
    ) => {
      if (isMulti) {
        const selectedOptions = newValue as MultiValue<OptionType>;
        if (selectedOptions && selectedOptions.some(option => option.value === 'add_new')) {
          setIsModalOpen(true);
        } else {
          setSelectedOptions(selectedOptions as OptionType[]);
          const selectedValues = selectedOptions.map((option) => option.value);
          onAccountChange(selectedValues);
          // REMOVED: localStorage logic
        }
      } else {
        const selectedOptionSingle = newValue as SingleValue<OptionType>;
        if (selectedOptionSingle && selectedOptionSingle.value === 'add_new') {
          setIsModalOpen(true);
        } else {
          setSelectedOptions(selectedOptionSingle ? [selectedOptionSingle] : []);
          const selectedValue = selectedOptionSingle ? selectedOptionSingle.value : '';
          onAccountChange(selectedValue);
          // REMOVED: localStorage logic
        }
      }
    };

    const handleModalClose = () => {
      setIsModalOpen(false);
    };

    const handleAddNew = (newAccount: Account) => {
      const newOption: OptionType = {
        value: newAccount.accountId || '',
        label: newAccount.accountName || '',
      };
      setIsModalOpen(false);
      setAccounts(prevAccounts => [...prevAccounts, newOption]);
      if (isMulti) {
        setSelectedOptions(prevSelected => [...prevSelected, newOption]);
        onAccountChange([...selectedOptions.map(option => option.value), newOption.value]);
      } else {
        setSelectedOptions([newOption]);
        onAccountChange(newOption.value);
      }
    };



    return (
      <div ref={ref} className="searchable-picker">
        {label && <label className="form-label">{label}</label>}
        <Select
          className="react-select-styled react-select"
          classNamePrefix="react-select"
          options={modifiedOptions}
          formatOptionLabel={formatOptionLabel}
          styles={customStyles}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          placeholder={placeholder || `Select ${label || 'Account'}`}
          value={isMulti ? selectedOptions : selectedOptions[0]}
          onChange={handleSelectChange}
          tabIndex={tabIndex}
          isClearable={isClearable}
          isDisabled={isDisabled}
          ref={internalSelectRef} // Use the forwarded ref or fallback to internal ref
          isMulti={isMulti} // Enable multi-select based on the isMulti prop
        />
        {error && <div className="invalid-feedback d-block">{error}</div>}
        <CustomModal
          title={`${'Add New'.toTranslatedDisplay()} ${(label || 'Account').toTranslatedDisplay()}`}
          isVisible={isModalOpen}
          onClose={handleModalClose}
          body={<AccountForm
            onSave={handleAddNew}
            onClose={handleModalClose}
            accountTypes={accountType ? [accountType] : undefined}
            accountCategories={accountCategory ? (Array.isArray(accountCategory) ? accountCategory : [accountCategory]) : undefined}
          />}
          id=""
          onSave={() => { }}
        />
      </div>
    );
  }
)
